import { useEffect, useMemo, useState } from "react";
import { Shield, Plus, Eye, EyeOff, ChevronLeft, ChevronRight } from "lucide-react";
import { useFinanceStore } from "../stores/finance-store";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { AddTransactionDialog } from "../components/AddTransactionDialog";
import { BalanceCardsRow } from "../components/dashboard/BalanceCardsRow";
import { DashboardChartsRow } from "../components/dashboard/DashboardChartsRow";
import { RecentTransactionsCard } from "../components/dashboard/RecentTransactionsCard";
import { Card, CardContent } from "../components/ui/card";
import { isExpenseForReports, isIncomeForReports } from "../lib/transaction-types";
import {
  filterByStatus,
  filterVisibleTransactions,
  getAnnualCashFlowData,
  getDashboardAccountBalances,
  getDashboardAccountReportTotals,
  getEffectiveTransactionDate,
  isDashboardVisibleAccount,
  isTransactionInMonth,
} from "../lib/finance-calculations";
import { transactionMatchesAccount } from "../lib/account-relations";
import { getTransactionCategory } from "../lib/category-relations";
import { formatCategoryName, parseCurrency, stringToColor } from "../lib/utils";

const UNCATEGORIZED_ID = "__uncategorized__";

function getCategoryDataForMonth(transactions, categories, type, realizadoOnly) {
  const categoryMap = new Map();

  filterByStatus(transactions, realizadoOnly)
    .filter((tx) => (type === "income" ? isIncomeForReports(tx) : isExpenseForReports(tx)))
    .forEach((tx) => {
      const category = getTransactionCategory(tx, categories);
      const name = formatCategoryName(category?.name || tx.category);
      const key = category?.id || name;
      const parent = category?.parent_id
        ? categories.find((item) => item.id === category.parent_id)
        : null;
      const current = categoryMap.get(key) || {
        name,
        value: 0,
        color: category?.color || parent?.color || stringToColor(name),
      };

      categoryMap.set(key, {
        ...current,
        value: current.value + parseCurrency(tx.amount),
      });
    });

  return Array.from(categoryMap.values()).sort((a, b) => b.value - a.value);
}

function buildCategoryTreeData(transactions, categories, accounts, type) {
  const scopedCategories = categories.filter((category) => category.type === type);
  const categoryById = new Map(scopedCategories.map((category) => [category.id, category]));
  const dashboardAccounts = accounts.filter(isDashboardVisibleAccount);
  const totalsById = new Map();
  const syntheticCategories = new Map();

  const getValidParent = (category) => {
    const parent = category?.parent_id ? categoryById.get(category.parent_id) : null;
    return parent?.type === category?.type ? parent : null;
  };

  const getAncestorPath = (category) => {
    const path = [];
    const visited = new Set();
    let current = category;

    while (current?.id && !visited.has(current.id)) {
      visited.add(current.id);
      path.unshift(current);
      current = getValidParent(current);
    }

    return path;
  };

  const addValue = (id, amount) => {
    totalsById.set(id, (totalsById.get(id) || 0) + amount);
  };

  transactions
    .filter((tx) => (type === "income" ? isIncomeForReports(tx) : isExpenseForReports(tx)))
    .filter((tx) => dashboardAccounts.some((account) => transactionMatchesAccount(tx, account, accounts)))
    .forEach((tx) => {
      const amount = parseCurrency(tx.amount);
      if (!amount) return;

      const category = getTransactionCategory(tx, categories);
      if (category?.id && category.type === type) {
        getAncestorPath(category).forEach((item) => addValue(item.id, amount));
        return;
      }

      const fallbackName = formatCategoryName(tx.category) || "Sem categoria";
      const fallbackId = fallbackName === "Sem categoria" ? UNCATEGORIZED_ID : `legacy:${type}:${fallbackName}`;
      if (!syntheticCategories.has(fallbackId)) {
        syntheticCategories.set(fallbackId, {
          id: fallbackId,
          name: fallbackName,
          type,
          parent_id: null,
          color: stringToColor(fallbackName),
          codigo: "",
        });
      }
      addValue(fallbackId, amount);
    });

  const activeIds = new Set([...totalsById.keys()]);
  const allCategories = [...scopedCategories, ...syntheticCategories.values()];
  const validIds = new Set(allCategories.map((category) => category.id));
  const childrenByParent = new Map();

  allCategories.forEach((category) => {
    if (!activeIds.has(category.id)) return;

    const parentId = category.parent_id && validIds.has(category.parent_id) && activeIds.has(category.parent_id)
      ? category.parent_id
      : null;

    if (!childrenByParent.has(parentId)) childrenByParent.set(parentId, []);
    childrenByParent.get(parentId).push(category);
  });

  const compareCategories = (a, b) => {
    const valueDiff = (totalsById.get(b.id) || 0) - (totalsById.get(a.id) || 0);
    if (Math.abs(valueDiff) > 0.00001) return valueDiff;
    return formatCategoryName(a.name).localeCompare(formatCategoryName(b.name), "pt-BR");
  };

  const buildNode = (category, depth = 0) => ({
    id: category.id,
    name: formatCategoryName(category.name),
    value: totalsById.get(category.id) || 0,
    color: category.color || stringToColor(category.name),
    depth,
    children: (childrenByParent.get(category.id) || [])
      .sort(compareCategories)
      .map((child) => buildNode(child, depth + 1)),
  });

  return (childrenByParent.get(null) || [])
    .sort(compareCategories)
    .map((category) => buildNode(category));
}

function getDailyCashFlowData(transactions, month, year, realizadoOnly) {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const dailyMap = new Map();

  for (let day = 1; day <= daysInMonth; day += 1) {
    dailyMap.set(day, {
      day,
      label: `${day}/${month + 1}`,
      income: 0,
      expense: 0,
      balance: 0,
    });
  }

  filterByStatus(transactions, realizadoOnly)
    .filter((tx) => isIncomeForReports(tx) || isExpenseForReports(tx))
    .forEach((tx) => {
      const date = new Date(getEffectiveTransactionDate(tx, realizadoOnly));
      const item = dailyMap.get(date.getDate());
      if (!item) return;

      const amount = parseCurrency(tx.amount);
      if (isIncomeForReports(tx)) {
        item.income += amount;
      } else if (isExpenseForReports(tx)) {
        item.expense += amount;
      }
    });

  let runningBalance = 0;
  return Array.from(dailyMap.values()).map((item) => {
    runningBalance += item.income - item.expense;
    return {
      ...item,
      balance: runningBalance,
    };
  });
}

export default function DashboardPage() {
  const [privacyMode, setPrivacyMode] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [dashboardRealizado, setDashboardRealizado] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  
  const {
    transactions,
    accounts,
    categories,
    fetchTransactions,
    fetchAccounts,
    fetchCategories,
    isLoading,
    isDecrypting,
  } = useFinanceStore();
  
  useEffect(() => {
    fetchTransactions();
    fetchAccounts();
    fetchCategories();
  }, [fetchTransactions, fetchAccounts, fetchCategories]);

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear((year) => year + 1);
    } else {
      setCurrentMonth((month) => month + 1);
    }
  };

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear((year) => year - 1);
    } else {
      setCurrentMonth((month) => month - 1);
    }
  };
  
  const monthlyTransactions = useMemo(() => {
    return transactions.filter((tx) => isTransactionInMonth(tx, currentMonth, currentYear, dashboardRealizado));
  }, [transactions, currentMonth, currentYear, dashboardRealizado]);

  const dashboardTransactions = useMemo(() => {
    return filterVisibleTransactions(filterByStatus(monthlyTransactions, dashboardRealizado));
  }, [monthlyTransactions, dashboardRealizado]);

  const accountIncomeTotals = useMemo(() => {
    return getDashboardAccountReportTotals(accounts, dashboardTransactions, "income");
  }, [accounts, dashboardTransactions]);

  const accountExpenseTotals = useMemo(() => {
    return getDashboardAccountReportTotals(accounts, dashboardTransactions, "expense");
  }, [accounts, dashboardTransactions]);

  const accountBalances = useMemo(() => {
    return getDashboardAccountBalances(accounts, transactions, dashboardRealizado, {
      month: currentMonth,
      year: currentYear,
    });
  }, [accounts, transactions, dashboardRealizado, currentMonth, currentYear]);

  const totalIncome = useMemo(() => {
    return accountIncomeTotals.reduce((acc, item) => acc + (Number(item.value) || 0), 0);
  }, [accountIncomeTotals]);

  const totalExpense = useMemo(() => {
    return accountExpenseTotals.reduce((acc, item) => acc + (Number(item.value) || 0), 0);
  }, [accountExpenseTotals]);

  const cashFlowData = useMemo(() => {
    return getDailyCashFlowData(monthlyTransactions, currentMonth, currentYear, dashboardRealizado);
  }, [monthlyTransactions, currentMonth, currentYear, dashboardRealizado]);

  const categoryDataExpense = useMemo(() => {
    return getCategoryDataForMonth(monthlyTransactions, categories, "expense", dashboardRealizado);
  }, [monthlyTransactions, categories, dashboardRealizado]);

  const categoryDataIncome = useMemo(() => {
    return getCategoryDataForMonth(monthlyTransactions, categories, "income", dashboardRealizado);
  }, [monthlyTransactions, categories, dashboardRealizado]);

  const incomeCategoryTree = useMemo(() => {
    return buildCategoryTreeData(dashboardTransactions, categories, accounts, "income");
  }, [dashboardTransactions, categories, accounts]);

  const expenseCategoryTree = useMemo(() => {
    return buildCategoryTreeData(dashboardTransactions, categories, accounts, "expense");
  }, [dashboardTransactions, categories, accounts]);

  const annualCashFlowData = useMemo(() => {
    return getAnnualCashFlowData(transactions, currentYear, dashboardRealizado);
  }, [transactions, currentYear, dashboardRealizado]);

  const monthName = new Date(currentYear, currentMonth, 1).toLocaleString("pt-BR", {
    month: "long",
    year: "numeric",
  });

  const getBalanceAccountHref = (item) => {
    const params = new URLSearchParams({
      account_id: item.account.id || "",
      month: String(currentMonth + 1),
      year: String(currentYear),
    });

    return `/account-statement?${params.toString()}`;
  };
  
  return (
    <div className="space-y-6 pb-20 lg:pb-0">
      {/* Header */}
      <div className="flex flex-wrap items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Dashboard</h1>
          <div className="flex items-center gap-2 mt-1 text-sm text-secondary">
            <Shield className="w-4 h-4" />
            <span>Dados criptografados E2EE</span>
          </div>
        </div>
        
        <div className="flex flex-wrap items-center justify-end gap-2">
          <div className="flex items-center gap-2 rounded-md border bg-card px-2.5 py-1.5">
            <span className={`text-xs font-medium ${dashboardRealizado ? "text-muted-foreground" : "text-foreground"}`}>
              Projetado
            </span>
            <Switch
              checked={dashboardRealizado}
              onCheckedChange={setDashboardRealizado}
              aria-label="Alternar dashboard entre projetado e realizado"
            />
            <span className={`text-xs font-medium ${dashboardRealizado ? "text-foreground" : "text-muted-foreground"}`}>
              Realizado
            </span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setPrivacyMode(!privacyMode)}
            data-testid="privacy-toggle"
          >
            {privacyMode ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </Button>
          <Button onClick={() => setShowAddDialog(true)} className="btn-glow" data-testid="add-transaction-btn">
            <Plus className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Nova Transação</span>
            <span className="sm:hidden">Nova</span>
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-3">
            <Button variant="outline" size="sm" onClick={handlePrevMonth} className="px-2 sm:px-3">
              <ChevronLeft className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Anterior</span>
            </Button>
            <span className="text-sm font-semibold capitalize sm:text-base">{monthName}</span>
            <Button variant="outline" size="sm" onClick={handleNextMonth} className="px-2 sm:px-3">
              <span className="hidden sm:inline">Próximo</span>
              <ChevronRight className="h-4 w-4 sm:ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Decrypting indicator */}
      {isDecrypting && (
        <div className="flex items-center gap-2 text-secondary animate-pulse">
          <Shield className="w-4 h-4" />
          <span className="text-sm font-mono">Descriptografando dados...</span>
        </div>
      )}
      
      <BalanceCardsRow 
        accountBalances={accountBalances}
        totalIncome={totalIncome} 
        totalExpense={totalExpense} 
        incomeCategoryTree={incomeCategoryTree}
        expenseCategoryTree={expenseCategoryTree}
        realizadoOnly={dashboardRealizado}
        getBalanceAccountHref={getBalanceAccountHref}
        privacyMode={privacyMode} 
        isLoading={isLoading} 
      />
      
      <DashboardChartsRow
        cashFlowData={cashFlowData}
        annualCashFlowData={annualCashFlowData}
        categoryDataExpense={categoryDataExpense}
        categoryDataIncome={categoryDataIncome}
        privacyMode={privacyMode}
        isLoading={isLoading}
      />

      <RecentTransactionsCard
        transactions={dashboardTransactions}
        categories={categories}
        privacyMode={privacyMode}
        isLoading={isLoading}
        onAddTransaction={() => setShowAddDialog(true)}
      />

      {/* Add Transaction Dialog */}
      <AddTransactionDialog 
        open={showAddDialog} 
        onOpenChange={setShowAddDialog}
      />
    </div>
  );
}
